import type { Edge } from "@cimara/sim";
import type { World } from "@cimara/world";
import { samplePath } from "./route";

export interface MeshData {
  positions: Float32Array;
  normals: Float32Array;
  indices: Uint32Array;
}

export interface RibbonOptions {
  /** Full ribbon width in metres, constant or per edge. */
  width: number | ((edge: Edge) => number);
  /** Lateral centre of the ribbon relative to the road centreline. */
  offset?: number | ((edge: Edge) => number);
  /** Vertical displacement from the road surface. */
  yOffset?: number;
}

function resolve(value: number | ((edge: Edge) => number), edge: Edge): number {
  return typeof value === "function" ? value(edge) : value;
}

/**
 * A flat triangle ribbon following a route. The road itself comes from
 * `buildRoadGeometry`; this helper builds verges, lane markings and terraces.
 */
export function buildRouteRibbon(
  world: World,
  routeId: number | undefined,
  options: RibbonOptions,
): MeshData {
  const route =
    (routeId === undefined ? undefined : world.getRoute(routeId)) ?? world.routes[0];
  if (!route) {
    return {
      positions: new Float32Array(0),
      normals: new Float32Array(0),
      indices: new Uint32Array(0),
    };
  }

  const edges: Edge[] = [];
  for (const edgeId of route.edgeIds) {
    const edge = world.getEdge(edgeId);
    if (edge) edges.push(edge);
  }

  let vertexCount = 0;
  let indexCount = 0;
  for (const edge of edges) {
    const pointCount = edge.points.length / 3;
    if (pointCount < 2) continue;
    vertexCount += pointCount * 2;
    indexCount += (pointCount - 1) * 6;
  }

  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const indices = new Uint32Array(indexCount);
  const yOffset = options.yOffset ?? 0;

  let vertexWrite = 0;
  let indexWrite = 0;

  for (const edge of edges) {
    const points = edge.points;
    const pointCount = points.length / 3;
    if (pointCount < 2) continue;

    const width = Math.max(0.01, resolve(options.width, edge));
    const centre = resolve(options.offset ?? 0, edge);
    const halfWidth = width / 2;
    const vertexStart = vertexWrite / 3;
    let lastLateralX = 1;
    let lastLateralZ = 0;

    for (let i = 0; i < pointCount; i += 1) {
      const previous = i > 0 ? i - 1 : i;
      const following = i < pointCount - 1 ? i + 1 : i;
      const a = previous * 3;
      const b = following * 3;

      let tx = points[b] - points[a];
      let tz = points[b + 2] - points[a + 2];
      const horizontal = Math.hypot(tx, tz);
      if (horizontal > 1e-9) {
        tx /= horizontal;
        tz /= horizontal;
        lastLateralX = -tz;
        lastLateralZ = tx;
      }

      const cx = points[i * 3];
      const cy = points[i * 3 + 1] + yOffset;
      const cz = points[i * 3 + 2];

      const leftOffset = centre + halfWidth;
      const rightOffset = centre - halfWidth;

      positions[vertexWrite] = cx + lastLateralX * leftOffset;
      positions[vertexWrite + 1] = cy;
      positions[vertexWrite + 2] = cz + lastLateralZ * leftOffset;
      normals[vertexWrite] = 0;
      normals[vertexWrite + 1] = 1;
      normals[vertexWrite + 2] = 0;
      vertexWrite += 3;

      positions[vertexWrite] = cx + lastLateralX * rightOffset;
      positions[vertexWrite + 1] = cy;
      positions[vertexWrite + 2] = cz + lastLateralZ * rightOffset;
      normals[vertexWrite] = 0;
      normals[vertexWrite + 1] = 1;
      normals[vertexWrite + 2] = 0;
      vertexWrite += 3;
    }

    for (let i = 0; i < pointCount - 1; i += 1) {
      const left = vertexStart + i * 2;
      const right = left + 1;
      const nextLeft = left + 2;
      const nextRight = left + 3;
      indices[indexWrite] = left;
      indices[indexWrite + 1] = nextLeft;
      indices[indexWrite + 2] = right;
      indices[indexWrite + 3] = right;
      indices[indexWrite + 4] = nextLeft;
      indices[indexWrite + 5] = nextRight;
      indexWrite += 6;
    }
  }

  return { positions, normals, indices };
}

export interface TreePlacement {
  x: number;
  y: number;
  z: number;
  scale: number;
  rotation: number;
  tint: number;
}

export interface HillPlacement {
  x: number;
  y: number;
  z: number;
  radius: number;
  height: number;
  rotation: number;
  tint: number;
}

function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Deterministic tree placement on the verges. No assets, no random calls at
 * render time, and no trees on the road.
 */
export function buildTreePlacements(
  world: World,
  routeId: number | undefined,
  seed = 0x51c1a4,
): TreePlacement[] {
  const route =
    (routeId === undefined ? undefined : world.getRoute(routeId)) ?? world.routes[0];
  if (!route) return [];

  const random = mulberry32(seed);
  const trees: TreePlacement[] = [];

  const place = (edgeId: number, s: number, side: number): void => {
    const sample = samplePath(world, edgeId, s);
    if (!sample) return;
    const lateral = side * (sample.width / 2 + 4.5 + random() * 20);
    trees.push({
      x: sample.x + sample.lateralX * lateral,
      y: sample.y - 0.12,
      z: sample.z + sample.lateralZ * lateral,
      scale: 0.75 + random() * 1.35,
      rotation: random() * Math.PI * 2,
      tint: random() * random(),
    });
  };

  for (const edgeId of route.edgeIds) {
    const edge = world.getEdge(edgeId);
    if (!edge) continue;
    for (let s = 4; s < edge.length - 4; s += 17) {
      const jitteredS = s + random() * 7;
      if (random() < 0.72) {
        place(edgeId, jitteredS, random() < 0.5 ? -1 : 1);
      }
      if (random() < 0.2) {
        place(edgeId, jitteredS + 2, random() < 0.5 ? -1 : 1);
      }
    }
  }

  return trees;
}

/**
 * Low-poly mountains in the interior of the loop. They sit on the valley floor
 * and give the chase camera a horizon to read against; no heightmap needed.
 */
export function buildHillPlacements(
  world: World,
  routeId: number | undefined,
  seed = 0x1a4c02,
): HillPlacement[] {
  const route =
    (routeId === undefined ? undefined : world.getRoute(routeId)) ?? world.routes[0];
  if (!route) return [];

  const random = mulberry32(seed);
  const centreX = (world.bounds.min.x + world.bounds.max.x) / 2;
  const centreZ = (world.bounds.min.z + world.bounds.max.z) / 2;
  const baseY = world.bounds.min.y - 38;
  const hills: HillPlacement[] = [];

  for (const edgeId of route.edgeIds) {
    const edge = world.getEdge(edgeId);
    if (!edge) continue;

    const tries = edge.length > 900 ? 2 : 1;
    for (let attempt = 0; attempt < tries; attempt += 1) {
      const s = edge.length * (0.28 + attempt * 0.42 + random() * 0.08);
      const sample = samplePath(world, edgeId, s);
      if (!sample) continue;

      let dx = centreX - sample.x;
      let dz = centreZ - sample.z;
      const distance = Math.hypot(dx, dz);
      if (distance < 1) continue;
      dx /= distance;
      dz /= distance;

      // Rotate the inward direction a little so the range is not a perfect ring.
      const angle = (random() - 0.5) * 1.1;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const dirX = dx * cos - dz * sin;
      const dirZ = dx * sin + dz * cos;
      const push = Math.min(distance * 0.72, 240 + random() * 420);

      hills.push({
        x: sample.x + dirX * push,
        y: baseY,
        z: sample.z + dirZ * push,
        radius: 100 + random() * 190,
        height: 80 + random() * 180,
        rotation: random() * Math.PI * 2,
        tint: random(),
      });
    }
  }

  return hills;
}
